/*
 * Sticky header dropdowns (Parakkat Jewels - Prestige theme)
 * ------------------------------------------------------------------
 * Default theme behaviour: a hover dropdown closes 250ms after the
 * mouse leaves it. This file keeps a top-level dropdown open until:
 *   - the mouse hovers another top-level menu item
 *   - the mouse hovers another header item (plain menu link, logo, search/account/cart icons)
 *   - the user clicks anywhere outside it
 *   - the user presses Esc, or keyboard focus moves away (theme)
 *   - the page is scrolled more than SCROLL_CLOSE_PX
 * Nested fly-outs inside a dropdown (e.g. Category > Rings) keep the theme's
 * normal behaviour. Touch devices are unaffected (the theme uses click there).
 */
(() => {
  const SCROLL_CLOSE_PX = 40;
  const TOP_LEVEL = '.header__primary-nav-item > details.header__menu-disclosure';
  const OTHER_HEADER_ITEMS = [
    '.header__primary-nav-item',
    '.header__primary-nav > button',
    '.header__secondary-nav a',
    '.header__secondary-nav button',
    '.header__logo'
  ].join(',');

  const isHover = (d) => d.trigger === 'hover';
  const isOpen = (d) => d.getAttribute('aria-expanded') === 'true';
  const openMenus = () => [...document.querySelectorAll(TOP_LEVEL)].filter(isOpen);
  const close = (d) => d.toggle(false);

  const setup = () => {
    document.querySelectorAll(TOP_LEVEL).forEach((menu) => {
      if (menu.dataset.stickyHover) return;
      menu.dataset.stickyHover = '';

      // When the mouse leaves, the theme starts a 250ms close timer.
      // Replaying a mouseover on the menu cancels that timer (theme API).
      menu.addEventListener('mouseout', () => {
        setTimeout(() => {
          if (isHover(menu) && isOpen(menu)) {
            menu.dispatchEvent(new MouseEvent('mouseover', { bubbles: false }));
          }
        }, 0);
      });

      // Remember scroll position when the menu opens.
      new MutationObserver(() => {
        if (isOpen(menu)) menu._openScrollY = window.scrollY;
      }).observe(menu, { attributes: true, attributeFilter: ['aria-expanded'] });
    });
  };

  // Hovering any other header item closes the open dropdown.
  document.addEventListener('mouseover', (event) => {
    const item = event.target.closest?.(OTHER_HEADER_ITEMS);
    if (!item) return;
    openMenus().forEach((menu) => {
      if (isHover(menu) && !menu.parentElement.contains(item)) close(menu);
    });
  });

  // Clicking anywhere outside the open dropdown closes it
  // (the theme only does this for click-to-open menus).
  document.addEventListener('click', (event) => {
    openMenus().forEach((menu) => {
      if (isHover(menu) && !menu.contains(event.target)) close(menu);
    });
  });

  // Esc closes the open dropdown even when keyboard focus is not inside it.
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    openMenus().forEach((menu) => {
      if (isHover(menu)) close(menu);
    });
  });

  // Scrolling the page closes the open dropdown.
  window.addEventListener('scroll', () => {
    openMenus().forEach((menu) => {
      if (isHover(menu) && Math.abs(window.scrollY - (menu._openScrollY ?? window.scrollY)) > SCROLL_CLOSE_PX) close(menu);
    });
  }, { passive: true });

  // Wait for the theme to register its menu elements so our listeners run after the theme's.
  customElements.whenDefined('dropdown-menu-disclosure').then(() => {
    setup();
    document.addEventListener('shopify:section:load', setup); // theme editor re-renders
  });
})();
